import { useEffect, useState } from 'react'
import { Form, Modal } from 'react-bootstrap'
import Skeleton from 'react-loading-skeleton'
import ApiChannels from 'assets/js/classes/ApiChannels'
import ApiEvents from 'assets/js/classes/ApiEvents'
import ApiRoutes from 'assets/js/classes/ApiRoutes'
import Submitter from 'assets/js/classes/Submitter'
import { AxiosMethods } from 'assets/js/utilities/axios'
import SubmitBtn from 'components/forms/SubmitBtn'
import ConfirmationAlert from 'components/ui/ConfirmationAlert'
import SmsBalanceAlert from 'components/ui/SmsBalanceAlert'
import ValidationAlert from 'components/ui/ValidationAlert'
import { stateUpdateSubmitBtn, useSubmitBtnDispatch, useSubmitBtnState } from 'contexts/SubmitBtnContext'
import { stateUpdateClientPhoneModal, useTaskModalDispatch, useTaskModalState } from 'contexts/TaskModalContext'
import { useTenantState } from 'contexts/TenantContext'
import useApiCall from 'hooks/useApiCall'
import useWebSocket from 'hooks/useWebSocket'

class Sms {
  static BALANCE_LOW_THRESHOLD = 500;
  static CHAR_LIMIT = 160;
}

export default function SendSmsForm({ task }) {
  // CONTEXT PARAMS - TASK MODAL
  const { showClientPhoneModal } = useTaskModalState();
  const taskModalDispatch = useTaskModalDispatch();

  // CONTEXT - TENANT
  const { tenant } = useTenantState();

  // CONTEXT - SUBMIT BUTTON
  const { btnState, response } = useSubmitBtnState();
  const submitBtnDispatch = useSubmitBtnDispatch();
  
  // STATE PARAMS
  const [message, setMessage] = useState('');
  const [showSmsBalanceAlert, setShowSmsBalanceAlert] = useState(false);
  const [balance, setBalance] = useState(undefined);
  const [loading, setLoading] = useState(true);

  // VALIDATION PARAMS
  const [formValidated, setFormValidated] = useState(false);
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const [validationMessage, setValidationMessage] = useState(null);

  // CONFIRMATION PARAMS
  const [confirmationMessage, setConfirmationMessage] = useState({});

  const sendNotification = async e => {
    e.preventDefault();
    
    setConfirmationMessage({});

    const submitter = new Submitter(e, 
      { stateUpdateSubmitBtn, submitBtnDispatch }, 
      { method: AxiosMethods.POST, route: ApiRoutes.TASK_SMS(task?.id) });

    await submitter.validate({setFormValidated, setShowValidationAlert, setValidationMessage});

    if (submitter.success) {
      setConfirmationMessage({
        title: 'Success',
        body: `Message to ${submitter.response?.data?.twilio_message?.to} ${submitter.response?.data?.twilio_message?.status}.`,
      });
    } else {
      setConfirmationMessage({});
    }
  }
  
  useApiCall({
    apiMethod: AxiosMethods.GET,
    apiRoute: ApiRoutes.CASHIER_CUSTOMER_BALANCE,
    callback: ({ balance }) => setBalance(balance),
    setLoading,
  });
  
  useWebSocket(ApiChannels.PRIVATE_TENANT(tenant?.id), [
    {
      events: [ApiEvents.CUSTOMER_BALANCE_CREDITED, ApiEvents.CUSTOMER_BALANCE_DEBITED],
      callback: ({ transaction }) => setBalance(balance => transaction?.ending_balance ?? 0),
    },
  ]);

  useEffect(() => {
    setMessage(
      `[${tenant?.name}] Request #${task.id}\n` +
      `Status: ${task.status}`
    );

    setShowSmsBalanceAlert(balance && balance * -1 < Sms.BALANCE_LOW_THRESHOLD);
  }, [balance, task, tenant]);

  return (
    <Modal 
      id="ModalSendSms" 
      show={showClientPhoneModal} 
      onHide={() => stateUpdateClientPhoneModal(taskModalDispatch, { showClientPhoneModal: false })} 
      size="lg" 
      keyboard={true}
    >
      <Modal.Header closeButton className="bg-white align-items-center">
        <Modal.Title className="flex-fill">
          Phone Notification to +{task?.client?.phone}
        </Modal.Title>
      </Modal.Header>

      <Form 
        autoComplete="off" 
        onSubmit={sendNotification} 
        noValidate
        validated={formValidated}
      >
        <Modal.Body>
          <Form.Control 
            as="textarea" 
            style={{height: '6rem'}}
            value={message}
            name="message"
            placeholder="Notification message..."
            onChange={e => setMessage(e.target.value)}
            required
          />

          <span>
            {Math.ceil(message.length / Sms.CHAR_LIMIT)} SMS ({message.length + '/' + Sms.CHAR_LIMIT} characters)
          </span>

          <p>
            Your current SMS balance is
            { loading
              ? <Skeleton height={22} inline />
              : <> &euro;{(balance * -1 / 100).toFixed(2)}</>
            }
          </p>

          <SmsBalanceAlert balance={balance}
            showSmsBalanceAlert={showSmsBalanceAlert} />
          
          <ValidationAlert 
            showValidationAlert={showValidationAlert} 
            validationMessage={validationMessage} />

          <ConfirmationAlert className="mb-1" message={confirmationMessage} />

        </Modal.Body>

        <Modal.Footer>  
          <SubmitBtn 
            disabled={message.length === 0}
            icon={['far', 'comment-sms']}
            loadStatus="Sending SMS ..."
            response={response} 
            state={btnState}
            text="Send SMS Notification"
          />
        </Modal.Footer>
      </Form>
    </Modal>
  )
}